// ── Разделы «Повторение», «Аудио», «Чтение» ──────────────────────────────────

import { useMemo } from "react";
import { SectionHeader, sectionBody, sectionBodyPhone } from "../components/Bits";
import { Heatmap } from "../components/Heatmap";
import { SrsPanel, TodayPlanCard, planMins, useTodayPlan } from "../components/Today";
import { t, uiLocale } from "../i18n";
import Icon from "../Icon";
import { PLAY_POS_KEY, fmtClock, lastPlayed, readPlayPos } from "../playPos";
import { buildStudyQueue, getDailyStats, getFsrsSettings, getNewMix, sortByDeckOrder } from "../srs";
import { SANS } from "../themes";
import { readableOn, shade } from "../ui/color";
import DueCount from "../ui/DueCount";
import { ListRow, Button as UiButton } from "../ui";
import { countWords, nCards, nWords, ruPlural } from "../utils";
import { usePalette } from "../ui/theme";
import { GamesCard } from "../games/GamesEntry";
import { chipFace, panelSurface } from '../ui/surface';

// ── Разделы «Повторение» и «Чтение» ──────────────────────────────────────────
// Четыре раздела приложения — Библиотека, Повторение, Чтение, Статистика — это
// отдельные экраны со своим адресом; меню (нижняя панель / боковая) видно на всех
// четырёх. Плеер, читалка и сам квиз — «экраны с фокусом»: без меню, со стрелкой
// «←», которая возвращает туда, откуда пришли.

// Колоды с карточками на сегодня и прогноз по дням — для списка повторения.
export function buildDeckData(projects){
  // Сегодняшняя очередь (как в самом повторении): новые — по порядку колод и до лимита.
  const st=getFsrsSettings();const daily=getDailyStats(st.dayStart);
  const rank=new Map();sortByDeckOrder(projects).forEach((p,i)=>(p.cards||[]).forEach(c=>rank.set(c.id,i)));
  const {queue}=buildStudyQueue(projects.flatMap(p=>p.cards||[]),{
    deckRank:c=>rank.has(c.id)?rank.get(c.id):1e9,dayStart:st.dayStart,newMix:getNewMix(),
    newQuota:st.newLimit>0?Math.max(0,st.newLimit-daily.newDone):Infinity,
    reviewQuota:st.reviewLimit>0?Math.max(0,st.reviewLimit-daily.reviewDone):Infinity,
  });
  const today=new Set(queue.map(c=>c.id));
  return projects.map(p=>{
    const cards=p.cards||[];
    const inToday=cards.filter(c=>today.has(c.id));
    const newC=inToday.filter(c=>(c.srs_state||"new")==="new");
    const learnC=inToday.filter(c=>["learn1","learn2"].includes(c.srs_state));
    const reviewC=inToday.filter(c=>c.srs_state==="review");
    const waiting=cards.filter(c=>(c.srs_state||"new")==="new"&&!today.has(c.id)).length;
    return {p,newC,learnC,reviewC,total:inToday.length,allCards:cards.length,waiting};
  }).filter(d=>d.total>0||d.waiting>0);
}
export function buildForecast(projects){
  const f={};const todayKey=new Date().toISOString().slice(0,10);
  projects.forEach(p=>(p.cards||[]).forEach(c=>{
    if(c.srs_due&&c.srs_due>todayKey){const k=c.srs_due.slice(0,10);f[k]=(f[k]||0)+1;}
  }));
  return f;
}

// «Повторение» — как главный экран Anki: дневная цель, колоды на сегодня, слова из читалки.
export function ReviewScreen({theme,navMode,projects,activeLang,onQuiz,onGames}){
  const C=usePalette();
  const deckData=useMemo(()=>buildDeckData(projects),[projects]);
  const forecast=useMemo(()=>buildForecast(projects),[projects]);
  const totalDue=deckData.reduce((s,d)=>s+d.total,0);
  const withCards=useMemo(()=>projects.filter(p=>(p.cards||[]).length>0),[projects]);
  const todayCards=useMemo(()=>projects.flatMap(p=>p.cards||[]),[projects]);
  const plan=useTodayPlan(todayCards);
  return(
    <div style={{minHeight:"100vh",background:C.backdrop,fontFamily:SANS}}>
      <div style={navMode?sectionBodyPhone:sectionBody}>
        <SectionHeader theme={theme} navMode={navMode} title={t("Повторение")} subtitle={plan.total?<><DueCount C={C} repeat={plan.repeat} fresh={plan.fresh} variant="words"/>{" · "}{t("≈ {0} мин",[planMins(plan.total)])}</>:withCards.length?t("На сегодня всё повторено"):t("Здесь появятся карточки из ваших аудиопроектов")}>
        </SectionHeader>
        <TodayPlanCard theme={theme} onQuiz={onQuiz} lang={activeLang} cards={todayCards}/>
        {onGames&&<GamesCard cards={todayCards} onGames={onGames}/>}
        {deckData.length?(
          <SrsPanel deckData={deckData} totalDue={totalDue} onQuiz={onQuiz} theme={theme} forecast={forecast} lang={activeLang} allDecks={withCards}/>
        ):(
          <div data-panel="" style={{...panelSurface(C),borderRadius:12,overflow:"hidden"}}>
            <div style={{padding:"12px 16px 8px",fontSize:13,fontWeight:600,color:C.mid}}>{t("Активность")}</div>
            <Heatmap theme={theme} forecast={forecast} lang={activeLang}/>
          </div>
        )}
      </div>
    </div>
  );
}

// «Аудио» — все аудио языка: продолжить последнее, ▶ играть здесь же, открыть плеер.
// Папки и действия над проектами — на главной; здесь только слушать.
export function AudioScreen({theme,navMode,projects,activeLang,onOpen,onPlayHere,playingId,onNew,onOpenQueue,queueCount=0}){
  const C=usePalette();
  const list=useMemo(()=>{
    let order={};try{Object.keys(JSON.parse(localStorage.getItem(PLAY_POS_KEY)||"{}")).forEach((id,i)=>{order[id]=i+1;});}catch{}
    return projects
      .filter(p=>(p.audio_path||p.local_audio_filename)&&(!activeLang||(p.src_lang||"").toLowerCase()===activeLang))
      .sort((a,b)=>(order[b.id]||0)-(order[a.id]||0)||new Date(b.updated_at||b.created_at||0)-new Date(a.updated_at||a.created_at||0));
  },[projects,activeLang]);
  const last=useMemo(()=>lastPlayed(list),[list]);
  const playBtn=(p,big)=>{
    const on=playingId===p.id;
    const chip=big||on?chipFace(C,{round:true}):null; // «Колода»: главная ▶ — покерная фишка
    return(
      <button onClick={e=>{e.stopPropagation();onPlayHere(p);}} aria-label={on?t("Пауза"):t("Играть «{0}»",[p.name])} title={on?t("Пауза"):t("Играть здесь")} className="ac-press" {...chip?.attrs}
        style={{width:big?44:40,height:big?44:40,borderRadius:"50%",border:"none",flexShrink:0,cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",padding:0,
          // в объёмной теме все ▶ — синие фишки с ребром и бликом (как кнопки), в плоских — только главная
          background:big||on||C.lift?C.blue:C.surface,color:big||on||C.lift?readableOn(C.blue):C.blueDark,
          boxShadow:C.lift?`0 3px 0 ${shade(C.blue)}, 0 0 14px -4px ${C.blue}`:undefined,...chip?.style}}>
        <Icon n={on?"pause":"play"} s={big?16:15} style={on?undefined:{marginLeft:2}}/>
      </button>
    );
  };
  const meta=(p)=>{const pos=readPlayPos(p.id);return[nCards(p.cardCount||(p.cards||[]).length),pos>0?t("остановились на {0}",[fmtClock(pos)]):null,p.folder||null].filter(Boolean).join(" · ");};
  const row=(p)=>(
    <ListRow key={p.id} onClick={()=>onOpen(p)} tip={t("Открыть плеер")} chevron before={playBtn(p,false)}
      title={p.name||t("Без названия")} subtitle={meta(p)}/>
  );
  return(
    <div style={{minHeight:"100vh",background:C.backdrop,fontFamily:SANS}}>
      <div style={navMode?sectionBodyPhone:sectionBody}>
        <SectionHeader theme={theme} navMode={navMode} title={t("Аудио")} subtitle={list.length?t("{0} аудио",[list.length]):t("Аудио с карточками по ходу прослушивания")}>
          {list.length>1&&<UiButton icon="list" onClick={onOpenQueue}>{t("Очередь")}{queueCount>0&&<span style={{color:C.blueDark,fontVariantNumeric:"tabular-nums",marginLeft:6}}>{queueCount}</span>}</UiButton>}
          <UiButton variant="primary" icon="plus" onClick={onNew}>{t("Новое аудио")}</UiButton>
        </SectionHeader>
        {last&&(
          <ListRow divider={false} strong onClick={()=>onOpen(last.p)} tip={t("Открыть плеер")} chevron before={playBtn(last.p,true)}
            title={<>{t("Продолжить:")} {last.p.name}</>} subtitle={meta(last.p)}
            data-panel="" style={{...panelSurface(C),borderRadius:12,overflow:"hidden",minHeight:64}}/>
        )}
        {list.length?(
          <div>
            <h2 style={{fontSize:13,fontWeight:600,color:C.mid,margin:"0 0 8px"}}>{t("Все аудио")}</h2>
            <div className="ac-list" data-panel="" style={{...panelSurface(C),borderRadius:12,overflow:"hidden"}}>{list.map(row)}</div>
          </div>
        ):(
          <div data-panel="" style={{textAlign:"center",padding:"40px 16px",color:C.mid,...panelSurface(C),borderRadius:12}}>
            <div style={{color:C.blueDark,marginBottom:10}}><Icon n="headphones" s={32} sw={1.5}/></div>
            <div style={{fontSize:17,fontWeight:600,color:C.text,marginBottom:6}}>{t("Начните с первого аудио")}</div>
            <div style={{fontSize:14,marginBottom:16}}>{t("Загрузите подкаст, аудиокнигу или запись — и добавляйте карточки прямо во время прослушивания.")}</div>
            <UiButton variant="primary" icon="plus" onClick={onNew}>{t("Новое аудио")}</UiButton>
          </div>
        )}
      </div>
    </div>
  );
}

// «Чтение» — свои тексты и книги: продолжить последний, открыть любой, добавить новый.
export function ReadingScreen({theme,navMode,textProjects,activeLang,onOpen,onNew,onImportBook}){
  const C=usePalette();
  const list=useMemo(()=>textProjects
    .filter(p=>!activeLang||(p.src_lang||"").toLowerCase()===activeLang)
    .sort((a,b)=>new Date(b.updated_at||0)-new Date(a.updated_at||0)),[textProjects,activeLang]);
  const words=useMemo(()=>Object.fromEntries(list.map(p=>[p.id,countWords(p.content||"",p.src_lang)])),[list]);
  let lastId=null;try{lastId=localStorage.getItem("ac_last_text");}catch{}
  const last=list.find(p=>p.id===lastId)||list[0];
  const date=(s)=>s?new Date(s).toLocaleDateString(uiLocale(),{day:"numeric",month:"short"}):"";
  const row=(p)=>(
    <ListRow key={p.id} onClick={()=>onOpen(p)} chevron
      leading={<span style={{width:38,height:38,borderRadius:9,background:C.surface,color:C.green,display:"inline-flex",alignItems:"center",justifyContent:"center"}}><Icon n="book" s={19}/></span>}
      title={p.name||t("Без названия")}
      subtitle={<>{nWords(words[p.id]||0)}{p.folder?` · ${p.folder}`:""}{p.updated_at?` · ${date(p.updated_at)}`:""}{p.youtube_id?t(" · с видео"):""}</>}/>
  );
  return(
    <div style={{minHeight:"100vh",background:C.backdrop,fontFamily:SANS}}>
      <div style={navMode?sectionBodyPhone:sectionBody}>
        <SectionHeader theme={theme} navMode={navMode} title={t("Чтение")} subtitle={list.length?`${list.length} ${ruPlural(list.length,"текст","текста","текстов")}`:t("Тексты и книги для чтения")}>
          {onImportBook&&<UiButton icon="upload" onClick={()=>onImportBook()}>{t("Книга из файла")}</UiButton>}
          <UiButton variant="primary" icon="plus" onClick={()=>onNew()}>{t("Новый текст")}</UiButton>
        </SectionHeader>
        {last&&(
          <ListRow divider={false} strong onClick={()=>onOpen(last)} chevron
            leading={<span {...chipFace(C,{round:true})?.attrs} style={{width:40,height:40,borderRadius:9,background:C.blue,color:"#fff",display:"inline-flex",alignItems:"center",justifyContent:"center",boxSizing:"border-box",...chipFace(C,{round:true})?.style}}><Icon n="play" s={16}/></span>}
            title={<>{t("Продолжить:")} {last.name||t("Без названия")}</>} subtitle={<>{t("Страница")} {(last.current_page||0)+1} · {nWords(words[last.id]||0)}</>}
            data-panel="" style={{...panelSurface(C),borderRadius:12,overflow:"hidden",minHeight:64}}/>
        )}
        {list.length?(
          <div>
            <h2 style={{fontSize:13,fontWeight:600,color:C.mid,margin:"0 0 8px"}}>{t("Все тексты")}</h2>
            <div className="ac-list" data-panel="" style={{...panelSurface(C),borderRadius:12,overflow:"hidden"}}>{list.map(row)}</div>
          </div>
        ):(
          <div data-panel="" style={{textAlign:"center",padding:"40px 16px",color:C.mid,...panelSurface(C),borderRadius:12}}>
            <div style={{color:C.green,marginBottom:10}}><Icon n="book" s={32} sw={1.5}/></div>
            <div style={{fontSize:17,fontWeight:600,color:C.text,marginBottom:6}}>{t("Начните с первого текста")}</div>
            <div style={{fontSize:14,marginBottom:16}}>{t("Вставьте статью или главу, загрузите книгу EPUB/TXT или привяжите видео с YouTube.")}</div>
            <UiButton variant="primary" icon="plus" onClick={()=>onNew()}>{t("Новый текст")}</UiButton>
          </div>
        )}
      </div>
    </div>
  );
}
